import { FormControl, FormGroup, Validators } from '@angular/forms'
import { BaseComponent } from '@core/classes/base-component'
import { Component, EventEmitter, Input, Output, ViewChild } from '@angular/core'
import { FilePickerError } from '@core/components/file-picker/file-picker.component'
import { LoaderService } from '@core/services/loader.service'
import { NotificationService } from '@core/services/notification.service'
import {
	AddressCreate,
	AddressesService,
	AddressRead,
	AddressReadList,
	AttachmentRead,
	AttachmentsService,
	ContainerReturnAddressWithTerminal,
	ContractorReadList,
	ContractorsService,
	ConveyanceTypeRead,
	EntityCreate,
	EntityRead,
	EntityReadList,
	EoriRead,
	FileRead,
	OrderCreate,
	OrderRead,
	OrdersService,
	OrderTemplateRead,
	OrderTemplateReadList,
	PickupTerminalCreate,
	PickupTerminalReadList,
	PriorityRead,
	ShipownerRead,
	TagRead,
	TagsService,
	UserMinimalDataRead,
	UserRead,
	UserReadList,
	UsersService,
	ValueDictionariesService,
	ValueDictionaryCreate,
	ValueDictionaryRead,
	ViesRead,
} from '@app/generated'
import { getItemTranslation } from '@app/general/utils/get-item-translation'
import { finalize, forkJoin, map, Observable } from 'rxjs'
import { Time } from '@core/classes/time'
import { AuthService } from '@app/auth/services/auth.service'
import { ContractorForm } from '@app/contractors/components/contractor-form/contractor-form.component'
import { TerminalForm } from '../terminal/terminal.form'
import { DeliveryAddressForm } from '../delivery-address/delivery-address.form'
import { LoadingPortForm } from '../loading-port/loading-port.form'
import { ShipForm } from '@app/orders/forms/ship/ship.form'
import { ModalService } from '@core/services/modal.service'
import { LanguageCode } from '@core/enums/language-code.enum'
import { AclService } from '@app/acl/services/acl.service'
import { Permission } from '@app/acl/enums/permissions.enum'
import { NullableBoolean, NullableNumber, NullableString } from '@core/types/nullable'
import { PickupTerminalRead } from '@app/generated/model/pickupTerminalRead'
import { OrderQueryParamKeysEnum } from '@app/orders/functions/orders.utils'

export const OrderFormLocalStorageKey = 'orders-form-type'

interface TempFileInterface {
	name: string
	response: FileRead
	tags?: FormControl<TagRead[]>
}

@Component({
	selector: 'order-form',
	templateUrl: './order.form.html',
	styleUrls: ['./order.form.scss'],
})
export class OrderForm extends BaseComponent {
	@Input() terminalForm: FormGroup = new FormGroup({})
	@Input() containerForm: FormGroup = new FormGroup({})
	@Input() addressForm: FormGroup = new FormGroup({})
	@Output() orderCreated: EventEmitter<boolean> = new EventEmitter<boolean>()

	@ViewChild('createContractorComponent') createContractorComponent?: ContractorForm
	@ViewChild('createTerminalComponent') createTerminalComponent?: TerminalForm
	@ViewChild('createDeliveryAddressComponent') createDeliveryAddressComponent?: DeliveryAddressForm
	@ViewChild('createReturnAddressComponent') createReturnAddressComponent?: DeliveryAddressForm
	@ViewChild('createLoadingPortComponent') createLoadingPortComponent?: LoadingPortForm
	@ViewChild('createShipComponent') createShipComponent?: ShipForm

	orderForm!: FormGroup<AddOrderFormCorrectSchema>
	orderTemplateForm!: FormGroup<AddOrderTemplateFormCorrectSchema>

	order?: OrderRead

	entities?: EntityReadList
	contractors?: ContractorReadList

	priorities: PriorityRead[] = []
	conveyanceTypes: ConveyanceTypeRead[] = []
	users: UserReadList = { total: 0, items: [] }
	terminals: PickupTerminalReadList = { total: 0, items: [] }
	deliveryAddresses: AddressReadList = { total: 0, items: [] }
	deliveryReturnAddresses: ContainerReturnAddressWithTerminal[] = []
	templates: OrderTemplateReadList = { total: 0, items: [] }
	ports: ValueDictionaryRead[] = []
	filteredPorts: ValueDictionaryRead[] = []
	fileTags: TagRead[] = []

	deliveryAddressControl = new FormControl<AddressRead | null>(null)
	returnAddressControl = new FormControl<ContainerReturnAddressWithTerminal | null>(null)
	loadingPortControl = new FormControl<ValueDictionaryRead | null>(null)
	loggedUser?: UserRead

	addingContractor = false
	addingTerminal = false
	addingAddress = false
	addingReturnAddress = false
	addLoadingPort = false

	pageLoaded = false

	tempFiles: TempFileInterface[] = []

	isNotModal = true

	// loaders for sub forms
	isCreatingPort = false
	isCreatingTerminal = false
	isCreatingEntity = false

	viesResult?: ViesRead
	entity?: EntityRead
	eoriResult?: EoriRead

	protected readonly textBasicData = $localize`Basic Data`
	protected readonly textConveyanceType = $localize`Conveyance type`
	protected readonly textOrderTemplate = $localize`Order Template`
	protected readonly textDateAndTimeOfOrderAcceptance = $localize`Date and Time of Order Acceptance`
	protected readonly textPriority = $localize`Priority`
	protected readonly textContractor = $localize`Contractor`
	protected readonly textAddNewContractor = $localize`Add New Contractor`
	protected readonly textContainer = $localize`Container`
	protected readonly textReceivingTerminal = $localize`Receiving Terminal`
	protected readonly textAddNewTerminal = $localize`Add New Terminal`
	protected readonly textAddNewPickupTerminal = $localize`Add new pickup terminal`
	protected readonly textShip = $localize`Ship`
	protected readonly textLoadingPort = $localize`Loading Port`
	protected readonly textDeliveryAddress = $localize`Delivery Address`
	protected readonly textAddNewReturnAddress = $localize`Add New Return Address`
	protected readonly textAddNewAddress = $localize`Add New Address`
	protected readonly textReturnAddressForEmptyContainer = $localize`Return Address for Empty Container`
	protected readonly textAddAnEmptyContainerReturnAddress = $localize`Add an Empty Container Return Address`
	protected readonly textAdditionalData = $localize`Additional Data`
	protected readonly textOrderAssignedTo = $localize`Order Assigned To`
	protected readonly textAssignToMe = $localize`Assign to Me`
	protected readonly textCommentsForOrder = $localize`Comments for Order`
	protected readonly textSelect = $localize`Select`
	protected readonly textEnterDate = $localize`Enter Date`
	protected readonly textEnterTime = $localize`Enter Time`
	protected readonly textTypeAndSelect = $localize`Type and Select`
	protected readonly textAddContract = $localize`Add Contract`
	protected readonly textAddNewLoadingPort = $localize`Add New Loading Port`
	protected readonly textReturnAddress = $localize`Return Address`
	protected readonly textComments = $localize`Comments`
	protected readonly textAddOrder = $localize`Add order`
	protected readonly textAddOrderTemplate = $localize`Add order template`
	protected readonly textAddAttachment = $localize`Add attachment`
	protected readonly textAcceptedFiles = $localize`Accepted Files`
	protected readonly textUnacceptedFiles = $localize`Unaccepted Files`
	protected readonly textEmptyEntity = $localize`Please select contractor first`
	protected readonly textEmptyTerminal = $localize`Please select the pickup terminal first`
	protected readonly textSubmit = $localize`Save and Select`
	protected readonly textBlocked = $localize` - Blocked`
	protected readonly textTerminalAddress = $localize`Terminal address`
	protected readonly textCarrierName = $localize`Carrier name`
	protected readonly textCarrierNamePlaceholder = $localize`Carrier name`
	protected readonly textDriverName = $localize`Driver's first and last name`
	protected readonly textDriverNamePlaceholder = $localize`Driver's first and last name`
	protected readonly textDriverData = $localize`Driver's data`
	protected readonly textDriverDataPlaceholder = $localize`Phone number, vehicle information, license plate number, etc.`
	protected readonly textLimitReached = $localize`- Contractor group container limit reached`
	protected readonly textClearOrderForm = $localize`Clear order form`
	protected readonly textDelete = $localize`Remove contractor`

	protected readonly getItemTranslation = getItemTranslation
	// end loaders
	private currentTerminalPortId?: string

	constructor(
		private loaderService: LoaderService,
		private notificationService: NotificationService,
		private ordersService: OrdersService,
		private valueDictionariesService: ValueDictionariesService,
		private usersService: UsersService,
		private contractorsService: ContractorsService,
		private authService: AuthService,
		private attachmentService: AttachmentsService,
		private tagsService: TagsService,
		private valueDictionaryService: ValueDictionariesService,
		private addressesService: AddressesService,
		private modalService: ModalService,
		private aclService: AclService,
	) {
		super()
		const urlParams = new URLSearchParams(window.location.search)
		const orderId = urlParams.get(OrderQueryParamKeysEnum.odderID)

		if (orderId) {
			this.ordersService.getOrder(orderId).subscribe({
				next: response => {
					this.order = response
				},
				error: () => {
					this.notificationService.error($localize`Failed to fetch contractor`)
				},
			})
		}
	}

	ngOnInit(): void {
		this.fetchData()
	}

	getOrderLocalStorage() {
		sessionStorage.getItem(OrderFormLocalStorageKey)
		const data = sessionStorage.getItem(OrderFormLocalStorageKey)
		return data ? JSON.parse(data).orderForm : null
	}

	setOrderLocalStorage(): void {
		sessionStorage.setItem(
			OrderFormLocalStorageKey,
			JSON.stringify({
				orderForm: this.orderForm.value,
			}),
		)
	}

	fetchData(): void {
		forkJoin({
			conveyanceTypes: this.ordersService.getConveyanceTypes(),
			priorities: this.ordersService.getPriorities(),
			ports: this.valueDictionariesService.getValueDictionaries('port', undefined),
			terminals: this.ordersService.getPickupTerminals(0, 15, undefined, false, 'desc', 'created_at'),
			entities: this.contractorsService.getEntities(0, 15, undefined, false, 'desc', 'created_at', false, true, true),
			users: this.usersService.getUsers(0, 15, undefined, false),
			templates: this.ordersService.getOrderTemplates(0, 15, undefined, 'desc', 'created_at'),
			fileTags: this.tagsService.getTags('attachment'),
		})
			.pipe(
				finalize(() => {
					this.disableLoaders()
				}),
			)
			.subscribe({
				next: data => {
					this.conveyanceTypes = data.conveyanceTypes
					this.priorities = data.priorities
					this.ports = data.ports
					this.filteredPorts = data.ports
					this.terminals = data.terminals
					this.entities = data.entities
					this.templates = data.templates
					this.orderForm = AddOrderFormCorrect()
					this.fileTags = data.fileTags
					this.setUsers(data.users)
					this.assignOrderToUser()
					this.setDefaultPriority()
					this.setFormListeners()
					this.setFormValidators()
					if (!this.isNotModal) {
						setTimeout(() => {
							const orderForm: JsonOrderFormCorrectSchema = this.getOrderLocalStorage()
							if (orderForm) this.setOrderForm(orderForm)
							this.orderForm.valueChanges.subscribe(value => {
								if (value) {
									this.setOrderLocalStorage()
								}
							})
						}, 500)
					} else if (this.order) {
						setTimeout(() => {
							const orderForm: JsonOrderFormCorrectSchema = this.createJsonOrderFormCorrectSchema(this.order!)
							this.setOrderForm(orderForm)
						}, 500)
						setTimeout(() => {
							if (this.order?.delivery_address_postal_code) {
								this.deliveryAddresses.items.forEach((deliveryAddress: AddressRead) => {
									if (
										deliveryAddress.street == this.order?.delivery_address_street &&
										deliveryAddress.city == this.order?.delivery_address_city &&
										deliveryAddress.postal_code == this.order?.delivery_address_postal_code &&
										deliveryAddress.country == this.order?.delivery_address_country
									) {
										this.deliveryAddressControl.setValue(deliveryAddress)
										this.deliveryAddressControl.updateValueAndValidity()
									}
								})
							}
							if (this.order?.return_address_postal_code) {
								this.deliveryReturnAddresses.forEach((deliveryAddress: ContainerReturnAddressWithTerminal) => {
									if (
										deliveryAddress.address.street == this.order?.return_address_street &&
										deliveryAddress.address.city == this.order?.return_address_city &&
										deliveryAddress.address.postal_code == this.order?.return_address_postal_code &&
										deliveryAddress.address.country == this.order?.return_address_country
									) {
										this.returnAddressControl.setValue(deliveryAddress)
										this.deliveryAddressControl.updateValueAndValidity()
									}
								})
							}
						}, 1000)
					}
				},
				error: () => {
					// TODO CLOSE MODAL
					this.notificationService.error($localize`Failed to fetch required data`)
				},
			})
	}

	fetchUsers(searchTerm?: string): void {
		this.usersService
			.getUsers(0, 15, searchTerm ? encodeURIComponent(searchTerm) : undefined, false)
			.pipe(finalize(this.disableLoaders))
			.subscribe({
				next: response => {
					this.setUsers(response)
				},
				error: error => {
					if (error.code == 422) {
						console.error('invalid query on fetch request')
					}
					const errorMessage = $localize`Error during fetching contractors`
					this.notificationService.error(errorMessage)
				},
			})
	}

	fetchTemplates(search?: string) {
		this.ordersService
			.getOrderTemplates(0, 15, search, 'desc', 'created_at')
			.pipe()
			.subscribe({
				next: response => {
					this.templates = response
				},
				error: error => {
					if (error.code == 422) {
						console.error('invalid query on fetch request')
					}
					const errorMessage = $localize`Error during fetching templates`
					this.notificationService.error(errorMessage)
				},
			})
	}

	fetchEntities(searchTerm?: string): void {
		this.contractorsService
			.getEntities(
				0,
				15,
				searchTerm ? encodeURIComponent(searchTerm) : undefined,
				false,
				'desc',
				'created_at',
				false,
				true,
			)
			.pipe(finalize(this.disableLoaders))
			.subscribe({
				next: response => {
					this.entities = response
				},
				error: error => {
					if (error.code == 422) {
						console.error('invalid query on fetch request')
					}
					const errorMessage = $localize`Error during fetching contractors`
					this.notificationService.error(errorMessage)
				},
			})
	}

	fetchTerminal(search?: string): void {
		this.ordersService
			.getPickupTerminals(0, 15, search ? encodeURIComponent(search) : undefined, false, 'desc', 'created_at')
			.pipe(finalize(this.disableLoaders))
			.subscribe({
				next: response => {
					this.terminals = response
				},
				error: error => {
					if (error.code == 422) {
						console.error('invalid query on fetch request')
					}
					const errorMessage = $localize`Error during fetching terminals`
					this.notificationService.error(errorMessage)
				},
			})
	}

	// TODO FIXME takedate
	getCreateRequest(): OrderCreate | undefined {
		const {
			takeDate,
			takeTime,
			enteringPortDate,
			containerWeight,
			deliveryAddressStreet,
			deliveryAddressBuildingNumber,
			deliveryAddressApartmentNumber,
			deliveryAddressCity,
			deliveryAddressPostalCode,
			deliveryAddressCountry,
			returnAddressStreet,
			returnAddressBuildingNumber,
			returnAddressApartmentNumber,
			returnAddressCity,
			returnAddressPostalCode,
		} = this.orderForm.value

		const parsedTakeDate = new Date(
			takeDate!.getFullYear(),
			takeDate!.getMonth(),
			takeDate!.getDate(),
			takeTime!.hour,
			takeTime!.minute,
		).toISOString()

		let parsedEnteringPortDate: string | null = null

		if (enteringPortDate) {
			const date = new Date(enteringPortDate)

			if (!isNaN(date.getTime())) {
				const year = date.getFullYear()
				const month = (date.getMonth() + 1).toString().padStart(2, '0')
				const day = date.getDate().toString().padStart(2, '0')

				parsedEnteringPortDate = `${year}-${month}-${day}`
			} else {
				console.error('Invalid date format:', enteringPortDate)
			}
		}

		console.log(parsedEnteringPortDate)

		return {
			conveyance_type_id: this.orderForm.value.conveyanceType?.id || undefined,
			take_date: parsedTakeDate,
			priority_id: this.orderForm.value.priority?.id || undefined,
			entity_id: this.orderForm.value.entity?.id || '',
			container_number: this.orderForm.value.containerNumber || null,
			container_pin: this.orderForm.value.containerPin || null,
			container_weight: containerWeight || null,
			container_size: this.orderForm.value.containerSize
				? this.parseDictionaryToRequest(this.orderForm.value.containerSize)
				: null,
			container_bl: this.orderForm.value.containerBl || null,
			container_atb: this.orderForm.value.containerAtb || null,
			// TODO
			container_atc: this.orderForm.value.containerAtc || null,
			container_mrn: this.orderForm.value.containerMrn || null,
			container_dsk: this.orderForm.value.containerDsk || null,
			entering_port_date: parsedEnteringPortDate || null,
			pickup_terminal_id: this.orderForm.value.pickupTerminal?.id || undefined,
			ship_name: this.orderForm.value.shipName || null,
			ship_number: this.orderForm.value.shipNumber || null,
			shipowner_id: this.orderForm.value.shipowner?.id || undefined,
			loading_port: this.orderForm.value.loadingPort
				? this.parseDictionaryToRequest(this.orderForm.value.loadingPort)
				: null,
			delivery_address_street: deliveryAddressStreet || null,
			delivery_address_building_number: deliveryAddressBuildingNumber || null,
			delivery_address_apartment_number: deliveryAddressApartmentNumber || null,
			delivery_address_city: deliveryAddressCity || null,
			delivery_address_postal_code: deliveryAddressPostalCode || null,
			delivery_address_country: deliveryAddressCountry || null,
			return_address_street: returnAddressStreet || null,
			return_address_building_number: returnAddressBuildingNumber || null,
			return_address_apartment_number: returnAddressApartmentNumber || null,
			return_address_city: returnAddressCity || null,
			return_address_postal_code: returnAddressPostalCode || null,
			assigned_to_id: this.orderForm.value.assignedTo?.id || '',
			attachments: this.orderForm.value.attachments?.map(attachment => attachment.id) ?? [],
			comment: this.orderForm.value.comment || null,
			template_id: this.orderForm.value.template?.id || undefined,
			carrier_name: this.orderForm.controls.is_carrier.value ? this.orderForm.controls.carrier_name.value : undefined,
			driver_name: this.orderForm.controls.is_carrier.value ? this.orderForm.controls.driver_name.value : undefined,
			driver_data: this.orderForm.controls.is_carrier.value ? this.orderForm.controls.driver_data.value : undefined,
			package_count: this.orderForm.controls.packageCount.value ?? undefined,
			place_check_in: this.orderForm.controls.checkInPlace.value ?? undefined,
			status_id: this.order ? this.order?.status?.id ?? undefined : undefined,
			order_payment_status_id: this.order ? this.order?.order_payment_status?.id ?? undefined : undefined,
			container_logistic_status_id: this.order ? this.order?.container_logistic_status?.id ?? undefined : undefined,
			container_clearance_status_id: this.order ? this.order?.container_clearance_status?.id ?? undefined : undefined,
			order_id: this.order?.id ?? undefined,
		}
	}

	submitForm(): void {
		this.orderForm.markAllAsTouched()
		this.createOrder()
	}

	assignOrderToUser() {
		const user = this.authService.decodePayload()
		if (!user) {
			console.error('Failed to get user data')
			return
		}
		const [firstName, lastName] = (user.name ?? '').split(' ')
		this.loggedUser = {
			id: user.sub,
			first_name: firstName,
			last_name: lastName,
			email: user.email,
		} as UserRead
		setTimeout(() => {
			if (this.loggedUser) {
				this.orderForm.controls.assignedTo.setValue(this.loggedUser)
			}
		}, 500)
	}

	createEntity(): void {
		const errorMessage = $localize`Application error`

		this.createContractorComponent?.config.formGroup.markAllAsTouched()

		if (!this.createContractorComponent) {
			this.notificationService.error(errorMessage)
			return
		}
		const data: EntityCreate | undefined = this.createContractorComponent.getCreateRequest()

		if (!data) {
			this.notificationService.error(errorMessage)
			return
		}

		this.isCreatingEntity = true

		this.contractorsService.createEntity(data).subscribe({
			next: response => {
				this.orderForm.controls.entity.setValue(response, { emitEvent: false })
				this.notificationService.success($localize`Entity has been added`)
				this.entities?.items.unshift(response)
				setTimeout(() => {
					this.isCreatingEntity = false
					this.addingContractor = false
				}, 300)
			},
			error: error => {
				let message = $localize`Was not able to add entity`
				if (error.code === 422) {
					message = $localize`Form contains errors`
				}
				if (error.error?.detail === 'tax_identifier_exists') {
					message = $localize`Contractor with same taxpayer identifier already exists!`
				}
				this.isCreatingEntity = false
				this.addingContractor = false
				this.notificationService.error(message)
			},
		})
	}

	createTerminal(): void {
		const errorMessage = $localize`Application error`
		if (!this.createTerminalComponent) {
			this.notificationService.error(errorMessage)
			return
		}
		this.createTerminalComponent?.config.formGroup.markAllAsTouched()
		const data: PickupTerminalCreate | undefined = this.createTerminalComponent?.getCreateRequest()
		if (!data) {
			this.notificationService.error(errorMessage)
			return
		}
		this.isCreatingTerminal = true
		this.ordersService.createPickupTerminal(data).subscribe({
			next: response => {
				this.orderForm.controls.pickupTerminal.setValue(response, { emitEvent: false })
				this.notificationService.success($localize`Pickup terminal has been added`)
				this.fetchTerminal()
				setTimeout(() => {
					this.isCreatingTerminal = false
					this.addingTerminal = false
				}, 300)
			},
			error: error => {
				let message = $localize`Was not able to add pickup terminal`
				if (error.code === 422) {
					message = $localize`Form contains errors`
				}
				this.addingTerminal = false
				this.isCreatingTerminal = false
				this.notificationService.error(message)
			},
		})
	}

	selectTemplate(template: OrderTemplateRead) {
		this.orderForm.controls.priority.setValue(template.priority ?? null)
		this.orderForm.controls.conveyanceType.setValue(template.conveyance_type ?? null)
		this.assignOrderToUser()
		this.setDefaultPriority()
		this.setFormValidators()
		this.setFormListeners()
	}

	sendFile(files: File | FileList) {
		this.loaderService.enable()
		const list = files instanceof File ? [files] : files
		const tasks: Observable<TempFileInterface>[] = Array.from(list).map((file: File) =>
			this.attachmentService
				.uploadFile(file)
				.pipe(map(response => ({ name: file.name, response, tags: new FormControl() }))),
		)

		forkJoin(tasks)
			.pipe(
				finalize(() => {
					this.loaderService.disable()
				}),
			)
			.subscribe({
				next: fileResponses => {
					this.tempFiles.push(...fileResponses)
				},
				error: () => {
					this.notificationService.error($localize`Failed to upload file`)
				},
			})
	}

	fileErrorHandler(errors: FilePickerError[]) {
		console.log(errors)
		// TODO
		// this.fileError.emit(errors)
	}

	acceptAttachment(file: TempFileInterface): void {
		const tags = file.tags?.value
		this.attachmentService
			.createAttachment({
				file_name: file.response.file_name,
				display_name: file.name,
				tag_ids: tags ? tags.map(tag => tag.id) : [],
			})
			.subscribe({
				next: response => {
					if (!this.orderForm.controls.attachments.value) {
						this.orderForm.controls.attachments.setValue([])
					}
					this.orderForm.controls.attachments.setValue(this.orderForm.controls.attachments.value!.concat(response))
					this.tempFiles.splice(this.tempFiles.indexOf(file), 1)
					this.notificationService.success($localize`Attachment has been added`)
				},
				error: () => {
					this.notificationService.error($localize`Was not able to add attachment`)
				},
			})
	}

	removeFile(file: TempFileInterface) {
		this.tempFiles.splice(this.tempFiles.indexOf(file), 1)
	}

	removeAttachment(attachment: AttachmentRead): void {
		const value = this.orderForm.controls.attachments.value?.filter(att => att !== attachment)
		if (!value) {
			return
		}
		this.loaderService.enable()
		this.attachmentService
			.deleteAttachment(attachment.id)
			.pipe(
				finalize(() => {
					this.loaderService.disable()
				}),
			)
			.subscribe({
				next: () => {
					this.notificationService.success($localize`Attachment has been removed`)
				},
				error: () => {
					this.notificationService.error($localize`Failed to remove attachment`)
				},
			})
		this.orderForm.controls.attachments.setValue(value)
	}

	createLoadingPort() {
		if (!this.createLoadingPortComponent) {
			return
		}
		let formValid = true

		for (const language of this.createLoadingPortComponent.selectedLanguages) {
			language.control.markAllAsTouched()
			if (language.control.invalid) {
				formValid = false
			}
		}

		if (
			!formValid ||
			!this.createLoadingPortComponent.selectedLanguages.find(l => l.language === LanguageCode.English)
		) {
			this.notificationService.error($localize`Form contains errors`)
			return
		}

		const value: ValueDictionaryCreate = {
			value: this.createLoadingPortComponent.selectedLanguages.find(l => l.language === LanguageCode.English)!.control
				.value!,
			category: 'port',
			translations: this.createLoadingPortComponent.selectedLanguages
				.filter(l => l.language !== LanguageCode.English)
				.map(l => ({
					language_code: l.language.substring(0, 2),
					text: l.control.value!,
				})),
		}

		this.isCreatingPort = true
		this.valueDictionaryService.createValueDictionary(value).subscribe({
			next: response => {
				this.ports.unshift(response)
				this.loadingPortControl.setValue(response)
				this.orderForm.controls.loadingPort.setValue(response)
				setTimeout(() => {
					this.isCreatingPort = false
					this.addLoadingPort = false
				}, 300)
				this.notificationService.success($localize`Port has been added`)
			},
			error: () => {
				this.isCreatingPort = false
				this.addLoadingPort = false
				this.notificationService.error($localize`Was not able to create port`)
			},
		})
	}

	// TODO REFACTOR ONLY FOR DEWLIVERY NOW
	createDeliveryAddress() {
		const form = this.createDeliveryAddressComponent?.deliveryForm
		const entityId = this.orderForm.value.entity?.id
		if (!form || !entityId) {
			this.notificationService.error($localize`Application error`)
			return
		}

		if (form.invalid) {
			this.notificationService.error($localize`Form contains errors`)
			return
		}

		const data: AddressCreate = this.parseAddressToRequest(form)
		// TODO FIXME SORTING
		this.addressesService.createAddress(data).subscribe({
			next: response => {
				this.contractorsService.assignAdditionalAddress(entityId, response.id).subscribe({
					next: addressResponse => {
						if (addressResponse.additional_addresses?.length) {
							const newAddress = addressResponse.additional_addresses[addressResponse.additional_addresses.length - 1]
							this.deliveryAddresses.items.push(newAddress)
							const entity = this.entities?.items.find(e => e.id === this.orderForm.controls.entity.value?.id)
							if (entity) {
								if (!entity.additional_addresses) {
									entity.additional_addresses = []
								}
								entity.additional_addresses.push(newAddress)
							}
							if (addressResponse.additional_addresses?.length) {
								this.deliveryAddressControl.setValue(newAddress)
								this.orderForm.controls.deliveryAddressCity.setValue(data.city ?? '')
								this.orderForm.controls.deliveryAddressApartmentNumber.setValue(data.apartment_number ?? '')
								this.orderForm.controls.deliveryAddressStreet.setValue(data.street ?? '')
								this.orderForm.controls.deliveryAddressPostalCode.setValue(data.postal_code ?? '')
								this.orderForm.controls.deliveryAddressBuildingNumber.setValue(data.building_number ?? '')
								this.orderForm.controls.deliveryAddressCountry.setValue(data.country ?? '')
								this.addingAddress = false
							}
						}
						this.notificationService.success($localize`Address has been created and assigned to selected entity`)
					},
					error: () => {
						this.notificationService.error($localize`Was not able to assign an address to entity`)
					},
				})
			},
			error: () => {
				this.notificationService.error($localize`Was not able to create an address`)
			},
		})
	}

	//FIXME MOVE TO ADDRESS
	parseAddressToRequest(form: FormGroup): AddressCreate {
		return {
			street: form.value.addressStreet || '',
			building_number: form.value.addressBuildingNumber || '',
			apartment_number: form.value.addressApartmentNumber || null,
			city: form.value.addressCity || '',
			postal_code: form.value.addressPostalCode || '',
			country: form.value.addressCountry,
		}
	}

	createReturnAddress(): void {
		// const portId = this.orderForm.controls.pickupTerminal.value?.port?.id
		// if (!portId) {
		// 	this.notificationService.error($localize`Please pick up terminal first`)
		// 	return
		// }

		if (!this.createReturnAddressComponent) {
			this.notificationService.error($localize`Application error`)
			return
		}

		if (this.createReturnAddressComponent!.deliveryForm.invalid) {
			this.notificationService.error($localize`Form contains errors`)
			return
		}

		const data: AddressCreate = this.parseAddressToRequest(this.createReturnAddressComponent!.deliveryForm)
		data.is_return = true
		this.addressesService.createContainerReturnAddress(data).subscribe({
			next: response => {
				this.returnAddressControl.setValue({ address: response, pickup_terminal: null })
				this.addingReturnAddress = false
				this.fetchReturnAddresses()
				this.notificationService.success($localize`Return address has been created and assigned to selected port`)
			},
			error: () => {
				this.notificationService.error($localize`Failed to create return address`)
			},
		})
	}

	fetchReturnAddresses(): void {
		const portId = this.orderForm.controls.pickupTerminal.value?.port?.id

		if (!portId) {
			this.deliveryReturnAddresses = []
			return
		}

		if (this.currentTerminalPortId === portId) {
			return
		}

		this.currentTerminalPortId = portId
		this.addressesService
			.getPortContainerReturnAddressesWithTerminalAddresses(portId, undefined, 'desc', 'created_at')
			.subscribe({
				next: response => {
					this.deliveryReturnAddresses = response
				},
				error: () => {
					this.notificationService.error($localize`Failed to fetch return addresses`)
				},
			})
	}

	aclCanEntitiesCreate(): boolean {
		return this.aclService.hasPermission([Permission.ENTITIES_CREATE])
	}

	aclCanContainerSizesCreate(): boolean {
		return this.aclService.hasPermission([Permission.CONTAINER_SIZES_CREATE])
	}

	aclCanPickupTerminalsCreate(): boolean {
		return this.aclService.hasPermission([Permission.PICKUP_TERMINALS_CREATE])
	}

	aclCanDeliveryAddressCreate(): boolean {
		return this.aclService.hasPermission([Permission.ADDRESSES_CREATE])
	}

	aclCanLoadingPortCreate(): boolean {
		return this.aclService.hasPermission([Permission.PORTS_CREATE])
	}

	aclCanContractorCreate(): boolean {
		return this.aclService.hasPermission([Permission.CONTRACTORS_CREATE])
	}

	aclCanReturnAddressCreate(): boolean {
		return this.aclService.hasPermission([Permission.CONTAINER_RETURN_ADDRESSES_CREATE])
	}

	clearCarrierData(value: boolean) {
		if (!value) {
			this.orderForm.controls.carrier_name.patchValue(null)
			this.orderForm.controls.driver_name.patchValue(null)
			this.orderForm.controls.driver_data.patchValue(null)
		}
	}

	// TODO MOVE TO ORDER PAGE
	private createOrder(): void {
		const data = this.getCreateRequest()
		if (!data || this.orderForm.invalid) {
			this.notificationService.error($localize`Form contains errors`)
			return
		}

		this.loaderService.enable()
		console.log(data)
		this.ordersService
			.createOrder(data)
			.pipe(
				finalize(() => {
					this.loaderService.disable()
				}),
			)
			.subscribe({
				next: () => {
					if (this.modalService.modalRef?.componentInstance instanceof OrderForm) {
						this.modalService.modalRef.close()
					}
					this.orderCreated.emit(true)
					sessionStorage.removeItem(OrderFormLocalStorageKey)
					this.notificationService.success($localize`Order has been added`)
				},
				error: error => {
					let message = $localize`Was not able to add order`
					if (error.code === 422) {
						message = $localize`Form contains errors`
					}
					this.notificationService.error(message)
					this.loaderService.disable()
				},
			})

		return
	}

	private setUsers(response: UserReadList) {
		const logged = response.items.find(u => u.id === this.loggedUser?.id)
		if (logged) {
			this.users.items = response.items.splice(response.items.indexOf(logged), 0)
		} else if (this.loggedUser) {
			this.users.items = [this.loggedUser].concat(response.items)
		} else {
			this.users = response
		}
	}

	private disableLoaders = (): void => {
		setTimeout(() => {
			this.loaderService.disable()
			this.pageLoaded = true
		})
	}

	private parseDictionaryToRequest(dictionary: ValueDictionaryRead): string {
		return JSON.stringify({
			en: dictionary.value,
			...dictionary.translations?.reduce(
				(prev, translation) => ({ ...prev, [translation.language_code]: translation.text }),
				{},
			),
		})
	}

	private setFormListeners(): void {
		this.orderForm.controls.entity.valueChanges.subscribe(() => (this.addingContractor = false))
		this.orderForm.controls.pickupTerminal.valueChanges.subscribe(() => (this.addingTerminal = false))

		this.loadingPortControl.valueChanges.subscribe(() => {
			if (this.loadingPortControl.value?.value) {
				this.orderForm.controls.loadingPort.setValue(this.loadingPortControl.value)
			}
		})

		this.deliveryAddressControl.valueChanges.subscribe(() => {
			this.orderForm.controls.deliveryAddressCity.setValue(this.deliveryAddressControl.value?.city || null)
			this.orderForm.controls.deliveryAddressStreet.setValue(this.deliveryAddressControl.value?.street || null)
			this.orderForm.controls.deliveryAddressPostalCode.setValue(this.deliveryAddressControl.value?.postal_code || null)
			this.orderForm.controls.deliveryAddressApartmentNumber.setValue(
				this.deliveryAddressControl.value?.apartment_number || null,
			)
			this.orderForm.controls.deliveryAddressBuildingNumber.setValue(
				this.deliveryAddressControl.value?.building_number || null,
			)
			this.orderForm.controls.deliveryAddressCountry.setValue(this.deliveryAddressControl.value?.country || null)
		})

		this.returnAddressControl.valueChanges.subscribe(() => {
			this.orderForm.controls.returnAddressCity.setValue(this.returnAddressControl.value?.address.city || null)
			this.orderForm.controls.returnAddressStreet.setValue(this.returnAddressControl.value?.address.street || null)
			this.orderForm.controls.returnAddressPostalCode.setValue(
				this.returnAddressControl.value?.address.postal_code || null,
			)
			this.orderForm.controls.returnAddressApartmentNumber.setValue(
				this.returnAddressControl.value?.address.apartment_number || null,
			)
			this.orderForm.controls.returnAddressBuildingNumber.setValue(
				this.returnAddressControl.value?.address.building_number || null,
			)
			console.log(this.orderForm.value)
		})

		this.orderForm.controls.pickupTerminal.valueChanges.subscribe(() => {
			this.fetchReturnAddresses()
		})

		this.orderForm.controls.entity.valueChanges.subscribe(() => {
			setTimeout(() => {
				this.deliveryAddressControl.setValue(null)
				this.orderForm.controls.deliveryAddressCity.setValue(null)
				this.orderForm.controls.deliveryAddressStreet.setValue(null)
				this.orderForm.controls.deliveryAddressPostalCode.setValue(null)
				this.orderForm.controls.deliveryAddressApartmentNumber.setValue(null)
				this.orderForm.controls.deliveryAddressBuildingNumber.setValue(null)
				this.orderForm.controls.deliveryAddressCountry.setValue(null)

				this.returnAddressControl.setValue(null)
				this.orderForm.controls.returnAddressCity.setValue(null)
				this.orderForm.controls.returnAddressStreet.setValue(null)
				this.orderForm.controls.returnAddressPostalCode.setValue(null)
				this.orderForm.controls.returnAddressApartmentNumber.setValue(null)
				this.orderForm.controls.returnAddressBuildingNumber.setValue(null)

				this.orderForm.controls.deliveryAddressCity.updateValueAndValidity({ emitEvent: false })
				this.orderForm.controls.deliveryAddressStreet.updateValueAndValidity({ emitEvent: false })
				this.orderForm.controls.deliveryAddressPostalCode.updateValueAndValidity({ emitEvent: false })
				this.orderForm.controls.deliveryAddressBuildingNumber.updateValueAndValidity({ emitEvent: false })
				this.orderForm.controls.deliveryAddressCountry.updateValueAndValidity({ emitEvent: false })
				this.orderForm.controls.returnAddressCity.updateValueAndValidity({ emitEvent: false })
				this.orderForm.controls.returnAddressStreet.updateValueAndValidity({ emitEvent: false })
				this.orderForm.controls.returnAddressPostalCode.updateValueAndValidity({ emitEvent: false })
				this.orderForm.controls.returnAddressBuildingNumber.updateValueAndValidity({ emitEvent: false })

				const addresses: AddressRead | AddressRead[] | undefined | null =
					this.orderForm.controls.entity.value?.address || []
				let additionalAddresses: AddressRead[] | undefined | null =
					this.orderForm.controls.entity.value?.additional_addresses || []

				if (!Array.isArray(additionalAddresses)) {
					additionalAddresses = additionalAddresses ? [additionalAddresses] : []
				}

				const concatenatedAddresses: AddressRead[] = [
					...(Array.isArray(addresses) ? addresses : [addresses]),
					...additionalAddresses,
				]

				if (!concatenatedAddresses.length) {
					this.deliveryAddresses.items = []
					return
				}

				this.deliveryAddresses.items = concatenatedAddresses
			})
		})
		this.orderForm.controls.conveyanceType.valueChanges.subscribe(conveyanceType => {
			const containerControl = this.orderForm.controls.containerNumber

			containerControl?.clearValidators()
			if (conveyanceType?.is_container_number_required) {
				containerControl?.setValidators([
					Validators.required,
					Validators.pattern('\\b[A-Z]{4}\\s?\\d{7}\\b'),
					Validators.maxLength(50),
				])
			} else {
				containerControl?.setValidators([Validators.maxLength(50)])
			}

			containerControl?.updateValueAndValidity()
		})
	}

	// TODO REFACTOR
	private setFormValidators(): void {
		setTimeout(() => {
			if (this.createDeliveryAddressComponent) {
				this.createDeliveryAddressComponent.initValidation()
			}

			if (this.createReturnAddressComponent) {
				this.createReturnAddressComponent.initValidation()
			}

			if (this.createShipComponent) {
				this.createShipComponent.initValidation()
			}
		})
	}

	private setDefaultPriority() {
		const selected = this.priorities.find(p => p.is_default)
		if (!selected) {
			return
		}
		this.orderForm.controls.priority.setValue(selected)
	}

	checkContainerLimit(ent: EntityRead) {
		if (
			ent.contractor?.container_limit?.container_limit_max &&
			ent.contractor?.container_limit?.current_container_limit_max
		) {
			if (
				ent.contractor?.container_limit?.container_limit_max <=
				ent.contractor?.container_limit?.current_container_limit_max
			)
				return true
		}
		return false
	}

	checkIfUserIsAdmin() {
		const roles = this.authService.getUserRoleFromToken()
		if (roles)
			for (const role of roles) {
				for (const permission of role.permissions) {
					if (permission == 'default:admin') {
						return true
					}
				}
			}
		return false
	}

	filterPorts(search: string): void {
		this.filteredPorts = this.ports.filter(p => getItemTranslation(p).toLowerCase().includes(search.toLowerCase()))
	}

	private createJsonOrderFormCorrectSchema(order: OrderRead): JsonOrderFormCorrectSchema {
		const date = order?.entering_port_date ? new Date(order?.entering_port_date) : new Date()
		let loading_port = null
		if (order.loading_port != null) {
			const loadingPortTranslations = JSON.parse(order.loading_port)
			loading_port =
				this.ports.find(port =>
					port.translations?.some(
						translation =>
							translation.text === loadingPortTranslations.pl ||
							translation.text === loadingPortTranslations.en ||
							translation.text === loadingPortTranslations.de,
					),
				) ?? null
		}

		this.loadingPortControl.setValue(loading_port)

		return {
			conveyanceType: order.conveyance_type ?? null,
			template: null,
			takeDate: null,
			takeTime: null,
			priority: order.priority ?? null,
			entity: order.entity ?? null,
			containerNumber: order.container_number ?? null,
			containerPin: order.container_pin ?? null,
			containerWeight: order.container_weight ?? null,
			containerSize: null,
			containerBl: order.container_bl ?? null,
			containerAtb: order.container_atb ?? null,
			containerAtc: order.container_atc ?? null,
			containerMrn: order.container_mrn ?? null,
			containerDsk: order.container_dsk ?? null,
			enteringPortDate: date ?? null,
			pickupTerminal: order.pickup_terminal ?? null,
			shipName: order.ship_name ?? null,
			shipNumber: order.ship_number ?? null,
			shipowner: order.shipowner ?? null,
			loadingPort: loading_port,
			deliveryAddressStreet: order.delivery_address_street ?? null,
			deliveryAddressBuildingNumber: order.delivery_address_building_number ?? null,
			deliveryAddressApartmentNumber: order.delivery_address_apartment_number ?? null,
			deliveryAddressCity: order.delivery_address_city ?? null,
			deliveryAddressPostalCode: order.delivery_address_postal_code ?? null,
			deliveryAddressCountry: order.delivery_address_country ?? null,
			returnAddressStreet: order.return_address_street ?? null,
			returnAddressBuildingNumber: order.return_address_building_number ?? null,
			returnAddressApartmentNumber: order.return_address_apartment_number ?? null,
			returnAddressCity: order.return_address_city ?? null,
			returnAddressPostalCode: order.return_address_postal_code ?? null,
			assignedTo: order.assigned_to ?? null,
			attachments: null,
			comment: order.comment ?? null,
			carrier_name: order.carrier_name ?? null,
			driver_name: order.driver_name ?? null,
			driver_data: order.driver_data ?? null,
			is_carrier: !!order.carrier_name,
			packageCount: order.package_count ?? null,
			check_in_place: order.place_check_in ?? null,
		}
	}

	private setOrderForm(order: JsonOrderFormCorrectSchema) {
		const date = order?.takeDate ? new Date(order?.takeDate) : new Date()
		const time = new Time()
		time.hour = date.getHours()
		time.minute = date.getMinutes()
		time.format = '24'

		this.orderForm.controls.conveyanceType.setValue(order.conveyanceType ?? null)
		this.orderForm.controls.template.setValue(order.template ?? null)
		this.orderForm.controls.takeDate.setValue(date ?? null)
		this.orderForm.controls.takeTime.setValue(time ?? null)
		this.orderForm.controls.priority.setValue(order.priority ?? null)
		this.orderForm.controls.entity.setValue(order.entity ?? null)
		this.orderForm.controls.containerNumber.setValue(order.containerNumber ?? null)
		this.orderForm.controls.containerPin.setValue(order.containerPin ?? null)
		this.orderForm.controls.containerWeight.setValue(order.containerWeight ?? null)
		this.orderForm.controls.containerSize.setValue(order.containerSize ?? null)
		this.orderForm.controls.containerBl.setValue(order.containerBl ?? null)
		this.orderForm.controls.containerAtb.setValue(order.containerAtb ?? null)
		this.orderForm.controls.containerAtc.setValue(order.containerAtc ?? null)
		this.orderForm.controls.containerMrn.setValue(order.containerMrn ?? null)
		this.orderForm.controls.containerDsk.setValue(order.containerDsk ?? null)
		this.orderForm.controls.packageCount.setValue(order.packageCount ?? null)
		this.orderForm.controls.enteringPortDate.setValue(order.enteringPortDate ?? null)
		this.orderForm.controls.pickupTerminal.setValue(order.pickupTerminal ?? null)
		this.orderForm.controls.shipName.setValue(order.shipName ?? null)
		this.orderForm.controls.shipNumber.setValue(order.shipNumber ?? null)
		this.orderForm.controls.shipowner.setValue(order.shipowner ?? null)
		this.orderForm.controls.loadingPort.setValue(order.loadingPort ?? null)
		this.orderForm.controls.deliveryAddressStreet.setValue(order.deliveryAddressStreet ?? null)
		this.orderForm.controls.deliveryAddressBuildingNumber.setValue(order.deliveryAddressBuildingNumber ?? null)
		this.orderForm.controls.deliveryAddressApartmentNumber.setValue(order.deliveryAddressApartmentNumber ?? null)
		this.orderForm.controls.deliveryAddressCity.setValue(order.deliveryAddressCity ?? null)
		this.orderForm.controls.deliveryAddressPostalCode.setValue(order.deliveryAddressPostalCode ?? null)
		this.orderForm.controls.deliveryAddressCountry.setValue(order.deliveryAddressCountry ?? null)
		this.orderForm.controls.returnAddressStreet.setValue(order.returnAddressStreet ?? null)
		this.orderForm.controls.returnAddressBuildingNumber.setValue(order.returnAddressBuildingNumber ?? null)
		this.orderForm.controls.returnAddressApartmentNumber.setValue(order.returnAddressApartmentNumber ?? null)
		this.orderForm.controls.returnAddressCity.setValue(order.returnAddressCity ?? null)
		this.orderForm.controls.returnAddressPostalCode.setValue(order.returnAddressPostalCode ?? null)
		this.orderForm.controls.assignedTo.setValue(order.assignedTo ?? null)
		this.orderForm.controls.attachments.setValue(order.attachments ?? null)
		this.orderForm.controls.comment.setValue(order.comment ?? null)
		this.orderForm.controls.carrier_name.setValue(order.carrier_name ?? null)
		this.orderForm.controls.driver_name.setValue(order.driver_name ?? null)
		this.orderForm.controls.driver_data.setValue(order.driver_data ?? null)
		this.orderForm.controls.is_carrier.setValue(order.is_carrier ?? null)
		this.orderForm.controls.checkInPlace.setValue(order.check_in_place ?? null)
	}

	handleClearOrder() {
		sessionStorage.removeItem(OrderFormLocalStorageKey)
		this.orderForm = AddOrderFormCorrect()
		this.orderForm.updateValueAndValidity()
	}

	removeContractor() {
		this.orderForm.controls.entity.setValue(null)
	}

	showContractorNameWarning(): boolean {
		if (this.eoriResult && this.entity) {
			if (this.eoriResult.name && this.entity.name) {
				return this.compareNames(this.entity.name, this.eoriResult.name)
			} else return false
		} else return false
	}

	handleVIES(result: ViesRead): void {
		this.viesResult = result

		this.orderForm.controls.contractorViesValid.patchValue(result.valid ?? false, { emitEvent: false })
		this.orderForm.controls.contractorViesRequestDate.patchValue(result.request_date ?? null, {
			emitEvent: false,
		})
		this.orderForm.controls.contractorViesRequestIdentifier.patchValue(result.request_identifier ?? null, {
			emitEvent: false,
		})
	}

	getEntityVatNumber(): string | undefined {
		const tin = this.orderForm.controls.entity.value?.tax_identifier
		const country = this.orderForm.controls.entity.value?.address?.country

		if (tin && country) {
			const vatNumber = tin.replace(/ /g, '').replace(/-/g, '').replace(/_/g, '')
			return `${country.toUpperCase()}${vatNumber}`
		}

		return undefined
	}

	handleEORI(result: EoriRead): void {
		this.eoriResult = result

		this.orderForm.controls.contractorEoriNumber.patchValue(result.eori ?? null)
		this.orderForm.controls.contractorEoriRequestDate.patchValue(result.request_date ?? null)
		this.orderForm.controls.contractorEoriValid.patchValue(result.valid ?? null)
		this.orderForm.controls.contractorEoriStatus.patchValue(result.status ?? null)
		this.orderForm.controls.contractorEoriErrorDescription.patchValue(result.error_description ?? null)
		this.orderForm.controls.contractorEoriStatusDescription.patchValue(result.status_description ?? null)
		this.orderForm.controls.contractorEoriErrorReason.patchValue(result.error_reason ?? null)
		this.orderForm.controls.contractorEoriName.patchValue(result.name ?? null)
		this.orderForm.controls.contractorEoriAddress.patchValue(result.address ?? null)
		this.orderForm.controls.contractorEoriStreet.patchValue(result.street ?? null)
		this.orderForm.controls.contractorEoriPostalCode.patchValue(result.postal_code ?? null)
		this.orderForm.controls.contractorEoriCity.patchValue(result.city ?? null)
		this.orderForm.controls.contractorEoriCountry.patchValue(result.country ?? null)
	}

	private compareNames(x: string, y: string): boolean {
		if (x && y) {
			let result = true
			const z = y.replace(/,/g, '').replace(/_/g, '').replace(/./g, '')
			for (const part of z.split(' ')) {
				if (x.toLowerCase().search(part.toLowerCase()) < 0) {
					result = false
					break
				}
			}
			return result
		}
		return false
	}

	showPostalCodesWarning(): boolean {
		if (this.eoriResult && this.entity) {
			return this.eoriResult.postal_code?.toLowerCase() != this.entity.address?.postal_code.toLowerCase()
		}
		return false
	}

	showAddressesWarning(): boolean {
		return (
			this.showCitiesWarning() ||
			this.showStreetsWarning() ||
			this.showPostalCodesWarning() ||
			this.showCountriesWarning()
		)
	}

	showCitiesWarning(): boolean {
		if (this.eoriResult && this.entity) {
			return this.eoriResult.city?.toLowerCase() != this.entity.address?.city.toLowerCase()
		}
		return false
	}

	showStreetsWarning(): boolean {
		if (this.eoriResult && this.entity) {
			return this.eoriResult.street?.toLowerCase() != this.entity.address?.street.toLowerCase()
		}
		return false
	}

	showCountriesWarning(): boolean {
		if (this.eoriResult && this.entity) {
			return this.eoriResult.country?.toLowerCase() != this.entity.address?.country.toLowerCase()
		}
		return false
	}
}

// TODO MOVE THIS FILES TO SEPARATED FILE

export interface AddOrderFormCorrectSchema {
	conveyanceType: FormControl<ConveyanceTypeRead | null>
	template: FormControl<OrderRead | null>
	takeDate: FormControl<Date | null>
	takeTime: FormControl<Time | null>
	priority: FormControl<PriorityRead | null>
	entity: FormControl<EntityRead | null>

	containerNumber: FormControl<string | null>
	containerPin: FormControl<string | null>
	containerWeight: FormControl<number | null>
	containerSize: FormControl<ValueDictionaryRead | null>
	containerBl: FormControl<string | null>
	containerAtb: FormControl<string | null>
	containerAtc: FormControl<string | null>
	containerMrn: FormControl<string | null>
	containerDsk: FormControl<string | null>
	packageCount: FormControl<NullableNumber>

	enteringPortDate: FormControl<Date | null>

	pickupTerminal: FormControl<PickupTerminalRead | null>
	shipName: FormControl<string | null>
	shipNumber: FormControl<string | null>
	shipowner: FormControl<ShipownerRead | null>
	loadingPort: FormControl<ValueDictionaryRead | null>

	deliveryAddressStreet: FormControl<string | null>
	deliveryAddressBuildingNumber: FormControl<string | null>
	deliveryAddressApartmentNumber: FormControl<string | null>
	deliveryAddressCity: FormControl<string | null>
	deliveryAddressPostalCode: FormControl<string | null>
	deliveryAddressCountry: FormControl<string | null>

	returnAddressStreet: FormControl<string | null>
	returnAddressBuildingNumber: FormControl<string | null>
	returnAddressApartmentNumber: FormControl<string | null>
	returnAddressCity: FormControl<string | null>
	returnAddressPostalCode: FormControl<string | null>

	assignedTo: FormControl<UserRead | UserMinimalDataRead | null>
	attachments: FormControl<AttachmentRead[] | null>
	comment: FormControl<string | null>

	carrier_name: FormControl<string | null>
	driver_name: FormControl<string | null>
	driver_data: FormControl<string | null>
	is_carrier: FormControl<boolean | null>

	// VIES
	contractorViesRequestDate: FormControl<NullableString>
	contractorViesRequestIdentifier: FormControl<NullableString>
	contractorViesValid: FormControl<NullableBoolean>
	// EORI
	contractorEoriNumber: FormControl<NullableString>
	contractorEoriRequestDate: FormControl<NullableString>
	contractorEoriValid: FormControl<NullableBoolean>
	contractorEoriStatus: FormControl<NullableNumber>
	contractorEoriErrorDescription: FormControl<NullableString>
	contractorEoriStatusDescription: FormControl<NullableString>
	contractorEoriErrorReason: FormControl<NullableString>
	contractorEoriName: FormControl<NullableString>
	contractorEoriAddress: FormControl<NullableString>
	contractorEoriStreet: FormControl<NullableString>
	contractorEoriPostalCode: FormControl<NullableString>
	contractorEoriCity: FormControl<NullableString>
	contractorEoriCountry: FormControl<NullableString>
	checkInPlace: FormControl<NullableString>
}

export interface JsonOrderFormCorrectSchema {
	conveyanceType: ConveyanceTypeRead | null
	template: OrderRead | null
	takeDate: Date | null
	takeTime: Time | null
	priority: PriorityRead | null
	entity: EntityRead | null

	containerNumber: string | null
	containerPin: string | null
	containerWeight: number | null
	containerSize: ValueDictionaryRead | null
	containerBl: string | null
	containerAtb: string | null
	containerAtc: string | null
	containerMrn: string | null
	containerDsk: string | null
	packageCount: NullableNumber

	enteringPortDate: Date | null

	pickupTerminal: PickupTerminalRead | null
	shipName: string | null
	shipNumber: string | null
	shipowner: ShipownerRead | null
	loadingPort: ValueDictionaryRead | null

	deliveryAddressStreet: string | null
	deliveryAddressBuildingNumber: string | null
	deliveryAddressApartmentNumber: string | null
	deliveryAddressCity: string | null
	deliveryAddressPostalCode: string | null
	deliveryAddressCountry: string | null

	returnAddressStreet: string | null
	returnAddressBuildingNumber: string | null
	returnAddressApartmentNumber: string | null
	returnAddressCity: string | null
	returnAddressPostalCode: string | null

	assignedTo: UserRead | UserMinimalDataRead | null
	attachments: AttachmentRead[] | null
	comment: string | null

	carrier_name: string | null
	driver_name: string | null
	driver_data: string | null
	is_carrier: boolean | null
	check_in_place: string | null
}

export interface AddOrderTemplateFormCorrectSchema {
	name: FormControl<string | null>
}

// TODO ADD VALIDATORS
export const AddOrderFormCorrect = (order?: OrderRead): FormGroup<AddOrderFormCorrectSchema> => {
	const date = order?.take_date ? new Date(order?.take_date) : new Date()
	const time = new Time()
	time.hour = date.getHours()
	time.minute = date.getMinutes()
	time.format = '24'

	return new FormGroup(
		{
			conveyanceType: new FormControl<ConveyanceTypeRead | null>(order?.conveyance_type || null),
			template: new FormControl<OrderRead | null>(null),
			takeDate: new FormControl<Date | null>(date || null, [Validators.required]),
			takeTime: new FormControl<Time | null>(time, [Validators.required]),
			priority: new FormControl<PriorityRead | null>(order?.priority || null, [Validators.required]),
			entity: new FormControl<EntityRead | null>(order?.entity || null, [Validators.required]),
			containerNumber: new FormControl<string | null>(order?.container_number || null, [
				Validators.required,
				Validators.pattern('\\b[A-Z]{4}\\s?\\d{7}\\b'),
				Validators.maxLength(50),
			]),
			containerPin: new FormControl<string | null>(order?.container_pin || null, Validators.maxLength(50)),
			containerWeight: new FormControl<number | null>(order?.container_weight || null, [Validators.max(1000000)]),
			containerSize: new FormControl<ValueDictionaryRead | null>(null, Validators.maxLength(100)),
			containerBl: new FormControl<string | null>(order?.container_bl || null, Validators.maxLength(50)),
			containerAtb: new FormControl<string | null>(order?.container_atb || null, [
				Validators.maxLength(50),
				Validators.pattern(/\b(?:ATB)?\d{18}\b/),
			]),
			containerAtc: new FormControl<string | null>(order?.container_atb || null, [
				Validators.maxLength(50),
				Validators.pattern(/\b(?:ATC)?\d{18}\b/),
			]),
			containerMrn: new FormControl<string | null>(order?.container_atb || null, [
				Validators.maxLength(50),
				Validators.pattern(/\b\d{2}[A-Z0-9]{16}\b/),
			]),
			containerDsk: new FormControl<string | null>(order?.container_dsk || null, Validators.maxLength(50)),
			enteringPortDate: new FormControl<Date | null>(null),

			pickupTerminal: new FormControl<PickupTerminalRead | null>(order?.pickup_terminal || null),
			shipName: new FormControl<string | null>(order?.ship_name || null, Validators.maxLength(100)),
			shipNumber: new FormControl<string | null>(order?.ship_number || null, Validators.maxLength(100)),
			shipowner: new FormControl<ShipownerRead | null>(order?.shipowner || null),
			loadingPort: new FormControl<ValueDictionaryRead | null>(null, Validators.maxLength(100)),
			deliveryAddressStreet: new FormControl<string | null>(
				order?.delivery_address_street || null,
				Validators.maxLength(100),
			),
			deliveryAddressBuildingNumber: new FormControl<string | null>(
				order?.delivery_address_building_number || null,
				Validators.maxLength(100),
			),
			deliveryAddressApartmentNumber: new FormControl<string | null>(
				order?.delivery_address_apartment_number || null,
				Validators.maxLength(100),
			),
			deliveryAddressCity: new FormControl<string | null>(
				order?.delivery_address_city || null,
				Validators.maxLength(100),
			),
			deliveryAddressPostalCode: new FormControl<string | null>(
				order?.delivery_address_postal_code || null,
				Validators.maxLength(100),
			),
			deliveryAddressCountry: new FormControl<string | null>(order?.delivery_address_country || null),
			returnAddressStreet: new FormControl<string | null>(
				order?.return_address_street || null,
				Validators.maxLength(100),
			),
			returnAddressBuildingNumber: new FormControl<string | null>(
				order?.return_address_building_number || null,
				Validators.maxLength(100),
			),
			returnAddressApartmentNumber: new FormControl<string | null>(
				order?.return_address_apartment_number || null,
				Validators.maxLength(100),
			),
			returnAddressCity: new FormControl<string | null>(order?.return_address_city || null, Validators.maxLength(100)),
			returnAddressPostalCode: new FormControl<string | null>(
				order?.return_address_city || null,
				Validators.maxLength(100),
			),
			assignedTo: new FormControl<UserRead | UserMinimalDataRead | null>(order?.assigned_to || null, [
				Validators.required,
			]),
			attachments: new FormControl<AttachmentRead[]>([]),
			comment: new FormControl<string | null>(order?.comment || null, Validators.maxLength(8192)),
			carrier_name: new FormControl<string | null>(null),
			driver_name: new FormControl<string | null>(null),
			driver_data: new FormControl<string | null>(null),
			is_carrier: new FormControl<boolean | null>(false),
			packageCount: new FormControl<NullableNumber>(null, Validators.min(0)),
			// VIES
			contractorViesRequestDate: new FormControl<NullableString>(null),
			contractorViesRequestIdentifier: new FormControl<NullableString>(null),
			contractorViesValid: new FormControl<NullableBoolean>(null),
			contractorEoriNumber: new FormControl<NullableString>(null),
			contractorEoriRequestDate: new FormControl<NullableString>(null),
			contractorEoriValid: new FormControl<NullableBoolean>(null),
			contractorEoriStatus: new FormControl<NullableNumber>(null),
			contractorEoriErrorDescription: new FormControl<NullableString>(null),
			contractorEoriStatusDescription: new FormControl<NullableString>(null),
			contractorEoriErrorReason: new FormControl<NullableString>(null),
			contractorEoriName: new FormControl<NullableString>(null),
			contractorEoriAddress: new FormControl<NullableString>(null),
			contractorEoriStreet: new FormControl<NullableString>(null),
			contractorEoriPostalCode: new FormControl<NullableString>(null),
			contractorEoriCity: new FormControl<NullableString>(null),
			contractorEoriCountry: new FormControl<NullableString>(null),
			checkInPlace: new FormControl<NullableString>(null),
		},
		{
			updateOn: 'change',
		},
	)
}
